import React from "react";
import {
  Box,
  Typography,
  Grid2,
  Select,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ClearIcon from "@mui/icons-material/Clear";
import { tokens } from "../../Common/designTokens";

const gridStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 3,
  mb: 1,
};

export function TypeFilter({ value, onChange, options }) {
  const [inputValue, setInputValue] = React.useState(value || "");

  // Sync inputValue with value prop
  React.useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Create debounced onChange
  const debouncedOnChange = React.useCallback(
    React.useMemo(
      () => {
        const debounce = (func, delay) => {
          let timeoutId;
          return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
          };
        };
        return debounce(func => func(), 500); // 500ms delay
      },
      []
    ),
    []
  );

  // Filter out null/empty options (do NOT add "ALL")
  const filteredOptions = (options || []).filter(option => option && option.trim() !== "");

  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        Type
      </Typography>
      <Autocomplete
        value={value ?? ""}
        inputValue={inputValue}
        onChange={(event, newValue) => {
          const selectedValue = newValue || "";
          onChange({ target: { value: selectedValue } });
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          // For type filter, only update on selection, not on input change
          if (event && event.type !== "change") {
            const selectedValue = newInputValue || "";
            debouncedOnChange(() => onChange({ target: { value: selectedValue } }));
          }
        }}
        options={filteredOptions}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder="Select Type" 
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "51px",
                padding: "0px !important",
                backgroundColor: tokens.colors.white,
                borderRadius: "8px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              },
              "& .MuiOutlinedInput-input": {
                padding: "16.5px 14px"
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "9px"
              }
            }}
          />
        )}
        sx={{ 
          width: "466px",
        }}
        disableClearable={false}
        clearIcon={<ClearIcon fontSize="small" />}
        forcePopupIcon={true}
        popupIcon={null}
        componentsProps={{
          clearIndicator: {
            style: { visibility: (value && value !== "") ? 'visible' : 'hidden' },
            onClick: () => {
              onChange({ target: { value: "" } });
            }
          }
        }}
      />
    </Grid2>
  );
}

export function StateFilter({ value, onChange, options }) {
  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        State
      </Typography>
      <Select
        value={value}
        variant="outlined"
        required
        fullWidth
        onChange={onChange}
        sx={{
          textAlign: "left",
          width: "466px",
          "&.MuiSelect-select": { width: "100%" },
        }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { style: { maxHeight: "20ch" } },
        }}
        displayEmpty
        endAdornment={
          <InputAdornment position="end">
            {value !== null && value !== undefined && value !== "" && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { value: "" } });
                }}
                sx={{ marginRight: "8.5px" }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        }
      >
        <MenuItem value="" sx={{ display: "none" }}>
          Select State
        </MenuItem>
        {options.filter(option => option && option.value && option.value.trim() !== "" && option.name && option.name.trim() !== "").map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </Grid2>
  );
}

export function SingleSelectFilter({ label, value, onChange, options }) {
  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        {label}
      </Typography>
      <Select
        value={value || ""}
        variant="outlined"
        required
        fullWidth
        onChange={onChange}
        sx={{
          textAlign: "left",
          width: "466px",
          "&.MuiSelect-select": { width: "100%" },
        }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { style: { maxHeight: "20ch" } },
        }}
        displayEmpty
        endAdornment={
          <InputAdornment position="end">
            {value && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { value: "" } });
                }}
                sx={{ marginRight: "8.5px" }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        }
      >
        <MenuItem value="">All</MenuItem>
        {(options || []).filter(option => option !== null && option !== undefined && String(option).trim() !== "").map((option) => (
          <MenuItem key={String(option)} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Grid2>
  );
}

export function SideFilter({ value, onChange }) {
  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        Side
      </Typography>
      <Select
        value={value}
        variant="outlined"
        required
        fullWidth
        onChange={onChange}
        sx={{
          textAlign: "left",
          width: "466px",
          "&.MuiSelect-select": { width: "100%" },
        }}
        displayEmpty
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { style: { maxHeight: "20ch" } },
        }}
        endAdornment={
          <InputAdornment position="end">
            {value && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { value: "" } });
                }}
                sx={{ marginRight: "8.5px" }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        }
      >
        <MenuItem value="" sx={{ display: "none" }}>
          Select Side
        </MenuItem>
        <MenuItem value="BUY">BUY</MenuItem>
        <MenuItem value="SELL">SELL</MenuItem>
      </Select>
    </Grid2>
  );
}

export function BrokerDealerFilter({ value, onChange, options }) {
  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        Broker/Dealer
      </Typography>
      <Select
        value={value}
        variant="outlined"
        required
        fullWidth
        onChange={onChange}
        sx={{
          textAlign: "left",
          width: "466px",
          "&.MuiSelect-select": { width: "100%" },
        }}
        displayEmpty
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          PaperProps: { style: { maxHeight: "20ch" } },
        }}
        endAdornment={
          <InputAdornment position="end">
            {value && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { value: "" } });
                }}
                sx={{ marginRight: "8.5px" }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        }
      >
        <MenuItem value="" sx={{ display: "none" }}>
          Select Broker/Dealer
        </MenuItem>
        {options.filter(option => option && option.trim() !== "").map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Grid2>
  );
}

export function SingleDateFilter({ label, date, onDateChange }) {
  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        {label}
      </Typography>
      <Grid2 sx={{ width: "466px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={date}
            format="DD MMM YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                inputProps: { "aria-hidden": false },
                // Prevent immediate validation on each keystroke
                onKeyDown: (e) => {
                  // Don't immediately process individual keystrokes
                  e.stopPropagation();
                },
              },
              // Add a small delay to allow complete input
              field: {
                shouldRespectLeadingZeros: true,
              },
            }}
            onChange={(newDate) => {
              // Only update parent state if date is valid or null
              if (newDate === null || (newDate && newDate.isValid())) {
                onDateChange(newDate);
              }
            }}
            // Only validate on blur, not on each keystroke
            onAccept={(newDate) => {
              if (newDate === null || (newDate && newDate.isValid())) {
                onDateChange(newDate);
              }
            }}
          />
        </LocalizationProvider>
      </Grid2>
    </Grid2>
  );
}

export function ApplyClearButtons({ onApply, onClear, onSave, isEdit, hasValidationErrors = false, disableApply = false }) {
  return (
    <Grid2 sx={{...gridStyle, pt:2, mb:3}}>
      <Box sx={{ width: "148px", mr:"40px" }}>
        <Button
          variant="contained"
          color="none"
          fullWidth
          sx={{ 
            fontWeight: "bold",
            borderRadius: "8px",
            py: 1,
            bgcolor: tokens.colors.bodyBackgroundColor,
            color: hasValidationErrors ? tokens.colors.disabledFontColour : tokens.colors.fontColour,
            opacity: hasValidationErrors ? 0.6 : 1,
            height: "40px",
            '&:hover': {
              bgcolor: tokens.colors.white,
            }
          }}
          onClick={onSave}
          disabled={hasValidationErrors}
        >
          Save Filter
        </Button>
      </Box>
      <Box sx={{width: "200.8px" }}>
        <Button
          variant="outlined"
          // color="secondary"
          fullWidth
          sx={{ 
            borderRadius: "8px",
            py: 1,
            height: "40px",
          }}
          onClick={onClear}
        >
          Clear
        </Button>
      </Box>
      <Box sx={{ ml:5.2, width: "200.8px" }}>
        <Button
          variant="contained"
          color={hasValidationErrors || disableApply ? "inherit" : "primary"}
          fullWidth
          sx={{ 
            fontWeight: "bold",
            bgcolor: hasValidationErrors || disableApply ? tokens.colors.defaultGridHeaderBackground : tokens.palette.primary,
            color: hasValidationErrors || disableApply ? tokens.colors.disabledFontColour : tokens.colors.white,
            borderRadius: "8px",
            opacity: hasValidationErrors || disableApply ? 0.6 : 1,
            py: 1,
            height: "40px",
            '&:hover': {
              backgroundColor: hasValidationErrors || disableApply ? tokens.colors.defaultGridRowColor : tokens.palette.primaryA80,
            }
          }}
          onClick={onApply}
          disabled={hasValidationErrors || disableApply}
        >
          Apply
        </Button>
      </Box>
    </Grid2>
  );
}

export function DescriptionFilter({ value, onChange }) {
  const [localValue, setLocalValue] = React.useState(value || "");
  const MAX_LENGTH = 512;
  
  // Debounce the onChange callback
  const debouncedOnChange = React.useCallback(
    React.useMemo(
      () => {
        const debounce = (func, delay) => {
          let timeoutId;
          return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
          };
        };
        return debounce(onChange, 500); // 500ms delay
      },
      [onChange]
    ),
    [onChange]
  );
  
  // Sync local value with prop value
  React.useEffect(() => {
    setLocalValue(value || "");
  }, [value]);
  
  const handleChange = (e) => {
    let newValue = e.target.value || "";
    if (newValue.length > MAX_LENGTH) {
      newValue = newValue.slice(0, MAX_LENGTH);
    }
    setLocalValue(newValue); // Update local state immediately for UI responsiveness
    debouncedOnChange(newValue); // Debounce the actual onChange callback
  };

  const handlePaste = (e) => {
    // Prevent default paste then insert only allowed number of characters
    e.preventDefault();
    const pasteText = (e.clipboardData && e.clipboardData.getData('text')) || '';
    const available = MAX_LENGTH - (localValue ? localValue.length : 0);
    if (available <= 0) return;
    const toInsert = pasteText.slice(0, available);
    const newValue = (localValue + toInsert).slice(0, MAX_LENGTH);
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        Description
      </Typography>
      <TextField
        value={localValue}
        onChange={handleChange}
        onPaste={handlePaste}
        placeholder="Enter description"
        fullWidth
        inputProps={{ maxLength: MAX_LENGTH }}
        sx={{
          width: "466px",
          // Match styling used by other filters so adornments align consistently
          "& .MuiOutlinedInput-root": {
            height: "51px",
            padding: "0px !important"
          },
          "& .MuiOutlinedInput-input": {
            padding: "16.5px 14px"
          },
          // Ensure input adornment sits in the same place as other fields
          "& .MuiInputAdornment-root": {
            marginRight: "8.5px"
          }
        }}
        InputProps={{
          endAdornment: localValue && (
            <InputAdornment position="end">
              <IconButton 
                size="small" 
                onClick={() => {
                  setLocalValue("");
                  onChange("");
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Grid2>
  );
}

export function IssuerFilter({ value, onChange, options, hasError = false }) {
  const [inputValue, setInputValue] = React.useState(value || "");
  const [lastSubmittedValue, setLastSubmittedValue] = React.useState(value || "");
  
  // Sync inputValue with value prop
  React.useEffect(() => {
    setInputValue(value || "");
    setLastSubmittedValue(value || "");
  }, [value]);

  // Filter out null/empty options
  const filteredOptions = (options || []).filter(option => option && option.trim() !== "");

  const handleBlur = () => {
    // Only submit if the value has changed since last submission
    if (inputValue !== lastSubmittedValue) {
      onChange(inputValue || "");
      setLastSubmittedValue(inputValue);
    }
  };

  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px">
        Issuer
      </Typography>
      <Autocomplete
        value={value}
        inputValue={inputValue}
        onChange={(event, newValue) => {
          // This is called when an option is selected from the dropdown
          onChange(newValue || "");
          setLastSubmittedValue(newValue || "");
        }}
        onInputChange={(event, newInputValue) => {
          // Update local state for display purposes
          setInputValue(newInputValue);
          
          // Only trigger onChange when an option is selected, not while typing
          if (event && event.type !== "change") {
            // For selection events (not typing), update the parent state
            onChange(newInputValue || "");
            setLastSubmittedValue(newInputValue || "");
          }
        }}
        options={filteredOptions}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder="Select Issuer" 
            fullWidth
            error={hasError}
            helperText={hasError ? "No matching issuer found" : ""}
            onBlur={handleBlur} // Add blur handler here
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "51px",
                padding: "0px !important"
              },
              "& .MuiOutlinedInput-input": {
                padding: "16.5px 14px"
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "9px"
              }
            }}
          />
        )}
        sx={{ 
          width: "466px",
        }}
        freeSolo
        disableClearable={false}
        clearIcon={<ClearIcon fontSize="small" />}
        forcePopupIcon={true}
        popupIcon={null}
        componentsProps={{
          clearIndicator: {
            style: { visibility: value ? 'visible' : 'hidden' }
          }
        }}
      />
    </Grid2>
  );
}
